import React, { useCallback, useEffect, useState } from 'react';
import { Step, Stepper, StepLabel, Container, Stack, Typography, Button } from '@mui/material';
import { Box } from '@mui/system';
import { pdfjs } from 'react-pdf';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import AggrementAddStep1 from './AddEditForm/AggrementAddStep1';
import AggrementAddStep2 from './AddEditForm/AggrementAddStep2';
import AggrementAddStep3 from './AddEditForm/AggrementAddStep3';
import AggrementAddStep4 from './AddEditForm/AggrementAddStep4';
import AggrementAddStep5 from './AddEditForm/AggrementAddStep5';
import AggrementAddStep6 from './AddEditForm/AggrementAddStep6';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getAgreemetEditData, setAgreementEdit } from '../../store/Slice/Agreement/AgreementSlice';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function AddAgreement() {
  const [activeStep, setActiveStep] = useState(0);
  const [reviewData, setReviewData] = useState({});

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { rowId } = location?.state || {};

  const { LoginData } = useLoginSelector();
  const authReviewData = JSON.parse(localStorage.getItem('reviewData'));

  const handlePrev = () => {
    setActiveStep((activeStep) => activeStep - 1);
  };

  const getAgreementEditApi = useCallback(() => {
    if (location.pathname === '/agreement/edit' && rowId && LoginData && authReviewData === null) {
      const credential = {
        aggrementId: rowId,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId
      };
      dispatch(getAgreemetEditData(credential));
    } else {
      dispatch(setAgreementEdit(null)); // store in redux
    }
  }, [rowId, LoginData]);

  useEffect(() => {
    getAgreementEditApi();
  }, [getAgreementEditApi]);

  useEffect(() => {
    if (authReviewData !== null) {
      setReviewData(authReviewData);
    }
  }, []);

  const isAdd = location.pathname === '/agreement/add';

  return (
    <Page title="Partner Management System || Agreement">
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8, pt: 2.5 }}>
        <Container maxWidth="xl">
          {/* Header Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 3.5 }}
          >
            {/* Left: Back Button + Title + Subtitle */}
            <Stack spacing={0.5}>
              <Button
                onClick={() => navigate('/agreement')}
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                sx={{
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: '13px',
                  p: 0,
                  minWidth: 0,
                  mb: 0.5,
                  textTransform: 'none',
                  '&:hover': { color: '#0f172a', bgcolor: 'transparent' }
                }}
              >
                Back to Agreement List
              </Button>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: '#0f172a',
                  letterSpacing: '-0.5px'
                }}
              >
                {isAdd ? 'Add New Agreement' : 'Edit Agreement'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.25 }}>
                {isAdd
                  ? 'Configure bank, branch, documents, commission rates, and billing details.'
                  : 'Update partner agreement configuration, commission terms, and branch details.'}
              </Typography>
            </Stack>

            {/* Right: Breadcrumbs */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <BrudCrumbs />
              </Box>
            </Stack>
          </Stack>

          {/* Stepper Card */}
          <Box
            sx={{
              bgcolor: '#ffffff',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
              p: { xs: 2, md: 2.5 },
              mb: 3.5
            }}
          >
            <Stepper
              connector=""
              activeStep={activeStep}
              className="stepWizardOuter"
              sx={{
                bgcolor: 'transparent',
                p: 0,
                '& .MuiStep-root': {
                  p: { xs: 0.5, md: 1 }
                },
                '& .MuiStepLabel-label': {
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#64748b'
                },
                '& .MuiStepLabel-label.Mui-active': {
                  color: '#0284c7 !important',
                  fontWeight: 700
                },
                '& .MuiStepLabel-label.Mui-completed': {
                  color: '#0284c7 !important',
                  fontWeight: 600
                },
                '& .Mui-active .MuiStepIcon-root': {
                  color: '#0284c7 !important'
                },
                '& .Mui-completed .MuiStepIcon-root': {
                  color: '#0284c7 !important'
                }
              }}
            >
              <Step className="stepWizard" sx={{ mr: '0!important' }}>
                <StepLabel className="stepWizardLabel">Add Bank</StepLabel>
              </Step>

              <Step className="stepWizard" sx={{ mr: '0!important' }}>
                <StepLabel className="stepWizardLabel">Add Branch</StepLabel>
              </Step>

              <Step className="stepWizard" sx={{ mr: '0!important' }}>
                <StepLabel className="stepWizardLabel">Upload Agreement</StepLabel>
              </Step>

              <Step className="stepWizard" sx={{ mr: '0!important' }}>
                <StepLabel className="stepWizardLabel">Add Commission</StepLabel>
              </Step>

              <Step className="stepWizard" sx={{ mr: '0!important' }}>
                <StepLabel className="stepWizardLabel">Add Billing</StepLabel>
              </Step>

              <Step className="stepWizard" sx={{ mr: '0!important' }}>
                <StepLabel className="stepWizardLabel">Review</StepLabel>
              </Step>
            </Stepper>
          </Box>

          {activeStep === 0 ? (
            <AggrementAddStep1
              setActiveStep={setActiveStep}
              reviewData={reviewData}
              setReviewData={setReviewData}
            />
          ) : activeStep === 1 ? (
            <AggrementAddStep2
              handlePrev={handlePrev}
              setActiveStep={setActiveStep}
              reviewData={reviewData}
              setReviewData={setReviewData}
            />
          ) : activeStep === 2 ? (
            <AggrementAddStep4
              handlePrev={handlePrev}
              setActiveStep={setActiveStep}
              reviewData={reviewData}
              setReviewData={setReviewData}
            />
          ) : activeStep === 3 ? (
            <AggrementAddStep3
              handlePrev={handlePrev}
              setActiveStep={setActiveStep}
              reviewData={reviewData}
              setReviewData={setReviewData}
            />
          ) : activeStep === 4 ? (
            <AggrementAddStep6
              handlePrev={handlePrev}
              setActiveStep={setActiveStep}
              reviewData={reviewData}
              setReviewData={setReviewData}
            />
          ) : (
            <AggrementAddStep5 handlePrev={handlePrev} reviewData={reviewData} />
          )}
        </Container>
      </Box>
    </Page>
  );
}

export default AddAgreement;
